import { useId, type ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { IDENTITY_SEARCH_MIN_LENGTH } from "../../constants/identities.constants";
import {
  kycLevelToTier,
  kycStatusMeta,
  resolveIdentityDisplayName,
} from "../../constants/identityTiers.constants";
import type { Identity } from "../../types/identities-api.types";
import { resolveHolderEligibility } from "../../utils/holderEligibility";
import { HolderAvatar } from "../HolderAvatar";

type HolderPickerFieldProps = {
  /**
   * The server's page, rendered as received and in order — INCLUDING holders who cannot be issued a card.
   * Those are listed disabled, with the badge that says why. Hiding them made an existing person look like
   * one who had never been created, and pushed operators into creating a duplicate.
   */
  holders: Identity[];
  isLoading: boolean;
  selectedHolder: Identity | null;
  onSelect: (holderId: string | null) => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** Something was typed, but not enough characters to run a search yet. */
  isSearchTermTooShort: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  /**
   * Rendered on the label row, opposite the label. A node rather than an `onCreateHolder` callback, so the
   * picker stays ignorant of what the action opens — and the host can leave it out entirely when the user
   * lacks the permission to create holders.
   */
  action?: ReactNode;
};

export const HolderPickerField = ({
  holders,
  isLoading,
  selectedHolder,
  onSelect,
  query,
  onQueryChange,
  isSearchTermTooShort,
  hasMore,
  isLoadingMore,
  onLoadMore,
  action,
}: HolderPickerFieldProps) => {
  const { t } = useTranslations("cards");
  const labelId = useId();

  return (
    /*
      A `<span>` + `aria-labelledby`, not a `<label>`: this names a whole region, and the control it would
      point at — the search box — only exists while nothing is selected, so a `<label>` here would name
      nothing half the time.
    */
    <section className="space-y-2" role="group" aria-labelledby={labelId}>
      {/* Same label/affordance row as the alias field above, so the two read as one column. */}
      <div className="flex items-center justify-between gap-2">
        <span id={labelId} className="text-sm font-medium text-foreground">
          {t("create_card_label_holder")}
        </span>
        {action}
      </div>

      {selectedHolder && (
        <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
          {/* Derived, never hardcoded: the checkmark carries `aria-label="KYC verified"`, so asserting it
              on whatever happens to be selected would have the avatar claim a verification it never read. */}
          <HolderAvatar
            name={resolveIdentityDisplayName(selectedHolder)}
            tier={kycLevelToTier(selectedHolder.kyc_level)}
            verified={kycStatusMeta(selectedHolder.kyc_status).group === "approved"}
            size={36}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">
              {resolveIdentityDisplayName(selectedHolder)}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">{selectedHolder.email}</div>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {t("create_card_change_holder")}
          </button>
        </div>
      )}

      {!selectedHolder && (
        <>
          <ColocaSearchInput
            value={query}
            onValueChange={onQueryChange}
            placeholder={t("create_card_search_holder_placeholder")}
            aria-labelledby={labelId}
          />
          {isSearchTermTooShort && (
            <span className="block text-[11px] text-muted-foreground">
              {t("identities_search_min_length_hint").replace("{count}", String(IDENTITY_SEARCH_MIN_LENGTH))}
            </span>
          )}

          <div className="max-h-[220px] overflow-y-auto rounded-md border border-border bg-card">
            {isLoading && holders.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">{t("identities_loading")}</div>
            ) : holders.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">
                {query.trim() ? t("create_card_no_matching_holders") : t("create_card_no_eligible_holders")}
              </div>
            ) : (
              <>
                {holders.map((h) => {
                  const name = resolveIdentityDisplayName(h);
                  const { eligible, reason } = resolveHolderEligibility(h);
                  return (
                    /*
                      A native `disabled`, not `aria-disabled` with a no-op handler: the row is genuinely not
                      actionable, and `disabled` takes it out of the tab order instead of offering a focus
                      stop that does nothing. The badge travels inside the button, so a screen reader still
                      reads the reason when browsing the list.
                    */
                    <button
                      key={h.id}
                      type="button"
                      disabled={!eligible}
                      onClick={() => onSelect(h.id)}
                      className={cn(
                        "flex w-full items-center gap-3 border-b border-border p-2.5 text-left transition-colors last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                        eligible ? "hover:bg-muted/40" : "cursor-not-allowed opacity-60",
                      )}
                    >
                      <HolderAvatar
                        name={name}
                        tier={kycLevelToTier(h.kyc_level)}
                        verified={kycStatusMeta(h.kyc_status).group === "approved"}
                        size={32}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 truncate text-sm font-medium text-foreground">{name}</div>
                          {reason && (
                            <ColocaBadge variant={reason.tone} className="shrink-0">
                              {t(reason.labelKey as never)}
                            </ColocaBadge>
                          )}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {h.email}
                          {typeof h.cards_count === "number" && (
                            <>
                              {" "}
                              · {h.cards_count} {t("identities_label_cards")}
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* The picker is no longer capped at one page: paging continues
                    inside whatever the search narrowed it to. */}
                {hasMore && (
                  <div className="flex justify-center border-t border-border p-2">
                    <ColocaButton variant="default" size="sm" onClick={onLoadMore} loading={isLoadingMore}>
                      {isLoadingMore ? t("cards_loading_more") : t("cards_load_more")}
                    </ColocaButton>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};
